<template>
  <div>
    <TendersHeader @switch-view="$refs.views.openViewsSheet()" />
    <div class="tenders__main">
      <!-- White surface holding the view tabs + filter bar + search -->
      <div class="tenders__views-panel">
        <TenderViews ref="views" @select="onSelectView" @refresh="onViewRefresh" @menu-opened="closeFilterPopovers">
          <template #filters>
            <div v-if="activeView" class="tenders__filterbar">
              <TendersFilters
                ref="tendersFilters"
                :searchFilters="searchFilters"
                @clear-filter="onClearFilter"
                @zones-settings="zonesDialogOpen = true"
                @advanced-filters-change="updateSearchFilters"
                @platform-change="onPlatformsChange"
                @status-change="onStatusesChange"
                @closing-before-change="onClosingBeforeChange"
                @read-filter-change="onReadFilterChange"
                @follow-filter-change="onFollowFilterChange"
                @share-filter-change="onShareFilterChange"
                @assignees-filter-change="onAssigneesFilterChange"
                @relevance-score-filter-change="onRelevanceScoreFilterChange"
                @publication-change="onPublicationChange"
                @archived-change="onArchivedChange"
                @open-keywords="keywordsDrawerOpen = true"
                @seao-settings="seaoDialogOpen = true"
                @clear-all="onClearAllFilters"
              >
                <template v-if="isPhone" #search>
                  <BaseSearchBarNew class="tenders__views-search" v-bind="searchBar" @input="onSearch" />
                </template>
              </TendersFilters>
            </div>
          </template>
        </TenderViews>
        <BaseSearchBarNew
          v-if="activeView && !isPhone"
          class="tenders__views-search"
          v-bind="searchBar"
          @input="onSearch"
        />
      </div>

      <div v-show="activeView" class="tenders__platforms" ref="topAnchor"></div>
      <TendersDataTable
        v-if="activeView"
        :mode="mode"
        :tenders="tenders"
        @rowClicked="openTenderDetails"
        @open-opportunity="openOpportunityDetails"
        @pageChanged="changePage"
        @refresh-tenders="runActiveSearch"
        @load-more="loadMore"
        @export-tenders="exportTenders"
      />
      <v-overlay :value="pending" absolute opacity="0.5" color="#ffffff" z-index="1"></v-overlay>
    </div>

    <TenderZonesDialog
      v-if="zonesDialogOpen"
      :searchFilters="searchFilters"
      @apply="onZonesApply"
      @close-dialog="zonesDialogOpen = false"
    />

    <TenderKeywordsDrawer
      v-if="keywordsDrawerOpen"
      :searchFilters="searchFilters"
      @apply="onKeywordsApply"
      @close-dialog="keywordsDrawerOpen = false"
    />

    <SEAOFiltersDialog
      v-if="seaoDialogOpen"
      :searchFilters="searchFilters"
      @set-search-filters="onSEAOFIltersChange"
      @close-dialog="seaoDialogOpen = false"
    />
  </div>
</template>

<script>
import SEAOFiltersDialog from '@/components/page-tenders/SEAOFiltersDialog';
import TendersDataTable from '@/components/page-tenders/TendersDataTable.vue';
import TendersHeader from '@/components/page-tenders/TendersHeader';

import { Modes } from '@/models/modes';
import { workingToViewFilters, viewLabel } from '@/models/tender-views';
import DebounceMixin from '@/mixins/debounce-mixin';

const LAST_VIEW_STORAGE_KEY = 'tender-views.lastViewId';
import { mapActions, mapGetters } from 'vuex';
import { bus } from '@/main';
import BaseSearchBarNew from '@/components/base/BaseSearchBarNew.vue';
import TenderViews from '@/components/page-tenders/views/TenderViews.vue';
import TendersFilters from '@/components/page-tenders/views/TendersFilters.vue';
import TenderKeywordsDrawer from '@/components/page-tenders/views/TenderKeywordsDrawer.vue';
import TenderZonesDialog from '@/components/page-tenders/views/TenderZonesDialog.vue';
import { highlightPhoneRow } from '@/util/highlight-phone-row';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageTenders',
  mixins: [DebounceMixin],
  components: {
    BaseSearchBarNew,
    SEAOFiltersDialog,
    TenderZonesDialog,
    TenderViews,
    TendersFilters,
    TenderKeywordsDrawer,
    TendersDataTable,
    TendersHeader,
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('modes', ['mode']),
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('tenders', ['pending', 'tenders', 'page', 'nextPage', 'continuous']),
    ...mapGetters('searchFilters', ['searchFilters']),
    ...mapGetters('tenderViews', ['activeView', 'activeViewId', 'isActiveViewDirty', 'allViews']),
    isPhone() {
      return viewport.size === 'phone';
    },
    // Placeholder is scoped to the active view: "… dans « <view name> »…"
    searchPlaceholder() {
      if (this.activeView) {
        // Phones: shorter, beside the filters button.
        const key = this.isPhone ? 'search-placeholder-short' : 'search-placeholder';
        return this.$t(`page.tenders.views.${key}`, {
          name: viewLabel(this.activeView),
        });
      }
      return this.$t('page.tenders.search-bar-placeholder');
    },
    searchBar() {
      return {
        name: 'search-tenders',
        value: this.searchFilters.query,
        placeholder: this.searchPlaceholder,
        hasDebounce: true,
      };
    },
  },
  data() {
    return {
      lastViewedTenderId: null,
      prevRoute: null,
      keywordsDrawerOpen: false,
      seaoDialogOpen: false,
      zonesDialogOpen: false,
    };
  },
  watch: {
    // Keep refresh navigation aligned with every view-selection path. Some actions (create,
    // duplicate, delete fallback) select a view directly in Vuex instead of calling onSelectView.
    activeViewId(viewId) {
      if (!viewId) return;
      this.persistLastView(viewId);
      this.syncViewIdInUrl(viewId);
    },
    // Clear the transient query when the underlying mode changes (route navigation).
    // Explore shares the tenders Vuex list; this page is keep-alive so created()/initViewsFlow
    // does not re-run — reload the active view when we become the inbox again.
    mode(newMode, oldMode) {
      if (newMode === oldMode) return;
      this.setQueryFilters('');
      if (newMode === Modes.INBOX.name) {
        this.runActiveSearch(1);
      }
    },
  },
  methods: {
    ...mapActions('tenders', ['searchTenders', 'searchTendersThrough']),
    ...mapActions('exportTenders', ['getExportTenders']),
    ...mapActions('tenderViews', ['loadViews', 'selectView']),
    ...mapActions('searchFilters', [
      'setAdvancedSearchFilters',
      'setPlatforms',
      'setAdministrativeRegions',
      'setClassifications',
      'setReadFilters',
      'setShareFilters',
      'setFollowFilters',
      'setStatuses',
      'setClosingBefore',
      'setPublicationDates',
      'setAssigneesFilters',
      'setRelevanceScoreFilters',
      'setQueryFilters',
      'setArchived',
      'setAllKeywords',
    ]),
    exportTenders() {
      const mode = Modes[`${this.mode}`];
      this.getExportTenders({ mode, searchFilters: this.searchFilters }).catch((error) => {
        this.$toast.error(error);
      });
    },
    changePage(page) {
      this.runActiveSearch(page);
      this.$refs.topAnchor.scrollIntoView({ block: 'end', behavior: 'smooth', inline: 'start' });
    },
    onPlatformsChange(platforms) {
      this.setPlatforms(platforms);
      this.saveAndSearch();
    },
    onSEAOFIltersChange(filters) {
      this.setAdministrativeRegions({ platform: 'seao_v2', regions: filters.regions });
      this.setClassifications({ classifications: filters.classifications });
      this.saveAndSearch();
    },
    onStatusesChange(statuses) {
      this.setStatuses(statuses);
      this.saveAndSearch();
    },
    onClosingBeforeChange(closingBefore) {
      this.setClosingBefore(closingBefore);
      this.saveAndSearch();
    },
    // Chip handlers (payload already computed by TendersFilters).
    onPublicationChange(publicationDates) {
      this.setPublicationDates(publicationDates);
      this.saveAndSearch();
    },
    onArchivedChange(archived) {
      this.setArchived(archived);
      this.saveAndSearch();
    },
    onReadFilterChange(readFilters) {
      this.setReadFilters(readFilters);
      this.saveAndSearch();
    },
    onFollowFilterChange(followFilters) {
      this.setFollowFilters(followFilters);
      this.saveAndSearch();
    },
    onShareFilterChange(shareFilters) {
      this.setShareFilters(shareFilters);
      this.saveAndSearch();
    },
    onAssigneesFilterChange(assignees) {
      this.setAssigneesFilters(assignees);
      this.saveAndSearch();
    },
    onRelevanceScoreFilterChange(relevanceScoreRange) {
      this.setRelevanceScoreFilters(relevanceScoreRange);
      this.saveAndSearch();
    },
    // Debounce so a multi-select session fires one search once the user pauses, not one per
    // click (700ms clears typical click cadence). Tab switches/pagination stay immediate.
    saveAndSearch() {
      this.debounce(() => this.runActiveSearch(1), 700);
    },
    // Run the active view: send {viewId} when clean, else {filters} (tweaked working state,
    // or an unfiltered search when the user has no views yet).
    runActiveSearch(page = 1) {
      this.searchTenders({ page, pageSize: 25, ...this.activeSearch() });
    },
    activeSearch() {
      // Query + publication dates are transient (not persisted / not "dirty"), so send full
      // filters when any is set — otherwise a {viewId} search would drop them.
      const hasTransient = !!(
        this.searchFilters.query ||
        this.searchFilters.publishedAfter ||
        this.searchFilters.publishedBefore
      );
      if (this.activeViewId && !this.isActiveViewDirty && !hasTransient) return { viewId: this.activeViewId };
      return { filters: workingToViewFilters(this.searchFilters) };
    },
    // The phone list's next page. A failure shows in the list, with a retry.
    loadMore() {
      if (this.nextPage === null) return;
      this.searchTenders({ page: Number(this.nextPage), pageSize: 25, append: true, ...this.activeSearch() }).catch(
        () => {},
      );
    },
    // After an action that may change which tenders the view holds. The phone list reloads
    // every page it shows, so the reader keeps their place in it.
    refreshListedTenders() {
      if (!this.continuous || this.page <= 1) {
        this.runActiveSearch(this.page);
        return;
      }
      this.searchTendersThrough({ lastPage: this.page, pageSize: 25, ...this.activeSearch() }).catch((error) =>
        this.$toast.error(error),
      );
    },
    initViewsFlow() {
      this.loadViews().then((views) => {
        if (views && views.length) {
          const initialId = this.pickInitialViewId(views);
          this.selectView(initialId);
        }
        // No views → nothing to search; the bar (with the "+ Nouvelle vue" button) still shows.
        if (this.allViews.length) this.runActiveSearch(1);
      });
    },
    pickInitialViewId(views) {
      const queryId = this.$route.query.viewId;
      if (queryId && views.find((v) => v.id === queryId)) return queryId;
      let stored = null;
      try {
        stored = window.localStorage.getItem(LAST_VIEW_STORAGE_KEY);
      } catch (e) {
        // storage blocked (private mode / disabled) — fall through to the first view
      }
      if (stored && views.find((v) => v.id === stored)) return stored;
      return views[0].id;
    },
    persistLastView(viewId) {
      try {
        window.localStorage.setItem(LAST_VIEW_STORAGE_KEY, viewId);
      } catch (e) {
        // ignore storage failures (private mode, quota)
      }
    },
    // Keep ?viewId in the URL in sync with the active view so the link is shareable/bookmarkable and
    // a reload lands on the same view. replace (not push) keeps history clean; the guard avoids a
    // redundant-navigation error, and .catch swallows any router rejection.
    syncViewIdInUrl(viewId) {
      if (!viewId || this.$route.query.viewId === viewId) return;
      this.$router.replace({ query: { ...this.$route.query, viewId } }).catch(() => {});
    },
    // Re-run the active view after a reset/delete from TenderViews.
    onViewRefresh() {
      this.runActiveSearch(1);
    },
    // Opening a views menu (all-views / ⋯) must close any open filter popover.
    // The filter closes on document mousedown, but the BaseButton activators
    // suppress that event, so close it explicitly here.
    closeFilterPopovers() {
      if (this.$refs.tendersFilters) this.$refs.tendersFilters.closePop();
    },
    onSearch(query) {
      this.setQueryFilters(query);
      this.runActiveSearch(1);
    },
    // A view tab was clicked. Load its saved filters and run it against /tenders/search.
    onSelectView(view) {
      this.selectView(view.id);
      this.runActiveSearch(1);
    },
    // Remove a single filter dimension from a chip in TendersFilters.
    onClearFilter(dimension) {
      switch (dimension) {
        case 'platforms':
          this.setPlatforms([]);
          break;
        case 'statuses':
          this.setStatuses([]);
          break;
        case 'closing':
          this.setClosingBefore(null);
          break;
        case 'read':
          this.setReadFilters([]);
          break;
        case 'followed':
          this.setFollowFilters([]);
          break;
        case 'shared':
          this.setShareFilters([]);
          break;
        case 'regions':
          this.setAdministrativeRegions({ platform: 'seao_v2', regions: [] });
          this.setClassifications({ classifications: [] });
          break;
        case 'keywords':
          // "All my keywords" counts as a keywords filter too.
          this.setAllKeywords(false);
          this.clearAdvancedFilter('keywordIds');
          return;
        case 'categories':
          this.clearAdvancedFilter('procurementCategories');
          return;
        case 'match-context':
          this.clearAdvancedFilter('matchContexts');
          return;
        case 'zones':
          this.clearAdvancedFilter('zones');
          return;
        default:
          return;
      }
      this.saveAndSearch();
    },
    clearAdvancedFilter(key) {
      const f = this.searchFilters;
      this.setAdvancedSearchFilters({
        keywordIds: key === 'keywordIds' ? [] : f.keywordIds || [],
        matchContexts: key === 'matchContexts' ? [] : f.matchContexts || [],
        procurementCategories: key === 'procurementCategories' ? [] : f.procurementCategories || [],
        zones: key === 'zones' ? {} : f.zones || {},
      });
      this.saveAndSearch();
    },
    onClearAllFilters() {
      this.setPlatforms([]);
      this.setStatuses([]);
      this.setClosingBefore(null);
      this.setReadFilters([]);
      this.setFollowFilters([]);
      this.setShareFilters([]);
      this.setAssigneesFilters([]);
      this.setArchived(false);
      this.setPublicationDates({ publishedAfter: null, publishedBefore: null });
      this.setRelevanceScoreFilters([]);
      this.setAllKeywords(false);
      this.setAdministrativeRegions({ platform: 'seao_v2', regions: [] });
      this.setClassifications({ classifications: [] });
      this.setAdvancedSearchFilters({ keywordIds: [], matchContexts: [], procurementCategories: [], zones: {} });
      this.saveAndSearch();
    },
    // Apply from the zones dialog: update only zones, preserving the other advanced dimensions.
    onZonesApply({ zones, includeUnlocated }) {
      const f = this.searchFilters;
      this.setAdvancedSearchFilters({
        keywordIds: f.keywordIds || [],
        matchContexts: f.matchContexts || [],
        procurementCategories: f.procurementCategories || [],
        zones,
        includeUnlocated,
      });
      this.zonesDialogOpen = false;
      this.saveAndSearch();
    },
    updateSearchFilters(tempSearchFilters) {
      this.setAdvancedSearchFilters(tempSearchFilters);
      if (tempSearchFilters.allKeywords !== undefined) this.setAllKeywords(tempSearchFilters.allKeywords);
      this.saveAndSearch();
    },
    // Apply from the keywords drawer: update keywords + match contexts (+ all-keywords),
    // preserving the other advanced dimensions (categories, zones).
    onKeywordsApply({ keywordIds, matchContexts, allKeywords }) {
      const f = this.searchFilters;
      this.setAdvancedSearchFilters({
        keywordIds,
        matchContexts,
        procurementCategories: f.procurementCategories || [],
        zones: f.zones || {},
      });
      this.setAllKeywords(allKeywords);
      this.keywordsDrawerOpen = false;
      this.saveAndSearch();
    },
    openTenderDetails(tenderId) {
      this.$track.tenderView();
      this.$router.push({ name: 'tender-details', params: { id: tenderId } });
    },
    openOpportunityDetails(opportunity) {
      if (!opportunity) return;
      this.$router.push({
        name: 'opportunity-details',
        params: { opportunityId: opportunity.id },
      });
    },
    highlightTender(tenderId) {
      const elementId = `item-${tenderId}`;
      this.$nextTick(() => this.goToElement(elementId));
    },
    goToElement(elementId) {
      const element = document.getElementById(elementId);

      if (element) {
        const tr = element.closest('tr');
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        if (tr) {
          tr.className = 'highlight';
          setTimeout(() => {
            tr.className = '';
          }, 1200);
        } else {
          highlightPhoneRow(element);
        }
      }
    },
  },
  created() {
    // Load views from the API, select one, and run it via /tenders/search.
    this.initViewsFlow();

    bus.$on('last-viewed-tender-id', (id) => {
      this.lastViewedTenderId = id;
    });

    bus.$on('refresh-page-requested', () => {
      this.refreshListedTenders();
    });
  },
  activated() {
    if (this.lastViewedTenderId) {
      this.$nextTick(() => {
        this.highlightTender(this.lastViewedTenderId);
        this.lastViewedTenderId = null;
      });
    }
    // This page is kept alive (BaseLayout), so created()/initViewsFlow does NOT re-run when the
    // user returns to the inbox — the URL arrives bare. Mirror the active view back into it so the
    // ?viewId stays consistent on return, not just on first load and explicit view switches.
    // The tenders list itself is reloaded by the mode watcher (Explore shares that Vuex list).
    if (this.activeViewId) this.syncViewIdInUrl(this.activeViewId);
  },
};
</script>

<style lang="scss">
.tenders {
  &__main {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 24px 32px 32px 32px;

    .base-search-bar-container {
      margin-bottom: 8px;
    }

    @include screen-below('tablet') {
      padding-right: $phone-page-gutter;
      padding-left: $phone-page-gutter;
    }
  }

  &__platforms {
    align-self: center;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  // White surface behind the view tabs + filter bar, full-bleed like the page header
  &__views-panel {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    margin: -24px -32px 16px;
    padding: 10px 32px 12px;
    position: relative;
    z-index: 2;

    @include phone-filter-panel;
  }

  &__filterbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 4px;

    // Phones: no view tabs above it to keep off.
    @include screen-below('tablet') {
      padding-top: 0;
    }
  }

  // Search bar inside the views panel — fill/placeholder now come from BaseSearchBarNew's defaults;
  // only the inbox-specific density (icon offset, padding, font-size) is tweaked here.
  &__views-search.base-search-bar-container {
    margin-top: 10px;
    margin-bottom: 0;

    // Phones: beside the filters button.
    @include screen-below('tablet') {
      margin-top: 0;
    }

    .base-search-bar-container__icon {
      color: $color-slate-400;
      left: 13px;

      &.is-focused {
        color: $color-accent;
      }
    }

    .base-search-bar-container__input {
      padding-top: 9px;
      padding-bottom: 9px;
      padding-left: 38px;
      font-size: 13px;
    }
  }

  &__filterbar-label {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $color-slate-400;
  }
}

.v-dialog__content--active ~ .tenders {
  overflow: hidden;
}

// If BaseBannerFixed is present (.has-update on App.vue) we need to add spacing
.has-update .tenders {
  padding-top: 38px;
  transition: padding 250ms ease-out;

  &__sidebar {
    min-height: calc(var(--viewport-height) - 38px);
  }
}
</style>
